import { BundleQuestionFragment, isWeb } from "@stepapp/core";
import { CategoryType, V2CartQuestionsUpsert } from "@stepapp/core/apiTypes";
import {
  Block,
  BottomSheet,
  Button,
  Typography,
  palette,
  spacing,
  typographyVariants,
  useBreakpoints,
} from "@stepapp/ui";
import React from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet } from "react-native";
import Animated, { FadeIn } from "react-native-reanimated";
import RenderHTML from "react-native-render-html";
import SimpleAnswerItem from "./SimpleAnswerItem";

type CategoryQuestionAnswersSheetProps = {
  closeModal: () => void;
  isVisible: boolean;
  question?: BundleQuestionFragment;
  attributes: V2CartQuestionsUpsert[];
  setNewAttribute?: (attributes: V2CartQuestionsUpsert[]) => void;
  categoryType?: CategoryType;
};

const CategoryQuestionAnswersSheet = ({
  closeModal,
  isVisible = false,
  question,
  attributes,
  setNewAttribute,
  categoryType,
}: CategoryQuestionAnswersSheetProps) => {
  const { t } = useTranslation();
  const { width } = useBreakpoints();
  const [step, setStep] = useState(1);

  const handleContinue = useCallback(() => {
    const hasAboveOneQuantity =
      attributes?.reduce((sum, attribute) => {
        if (question?.id?.includes(attribute?.attributeId || "")) {
          return sum + (attribute?.quantity || 0);
        }
        return sum;
      }, 0) > 1;

    if (hasAboveOneQuantity && categoryType === CategoryType.Massage) {
      return setStep(2);
    }

    closeModal();
  }, [attributes, question, closeModal, categoryType]);

  const handleClose = useCallback(() => {
    closeModal();
    setStep(1);
  }, [closeModal]);

  const sortedAnswers = question?.answers?.concat()?.sort((a, b) => a.position - b.position);

  return (
    <BottomSheet
      containerStyle={styles.bottomSheetContainer}
      contentStyle={styles.bottomSheet}
      toggle={closeModal}
      enableScroll={false}
      visible={isVisible}
    >
      <ScrollView contentContainerStyle={styles.scrollStyles}>
        {step === 1 && (
          <>
            <Typography variant="newH2">{question?.name}</Typography>

            <Block marginTop={4}>
              <Typography variant="lightLabel" type="secondary">
                {question?.additionalDescription}
              </Typography>
            </Block>

            <Block marginTop={4}>
              {sortedAnswers?.map(({ id, name, description, isFeatured }) => {
                const isSelected = !!attributes?.find((a) => a?.valueId?.includes(id));
                return (
                  <Block key={id} marginBottom={isWeb ? 1 : 0}>
                    <SimpleAnswerItem
                      description={description}
                      id={id}
                      isFeatured={isFeatured}
                      setNewAttribute={setNewAttribute}
                      questionType={question?.type}
                      questionId={question?.id || ""}
                      selected={isSelected}
                      title={name}
                      attributes={attributes}
                      numericType={question?.numericType}
                    />
                  </Block>
                );
              })}
            </Block>

            <Block paddingTop={2}>
              <RenderHTML
                source={{ html: question?.wysiwygDescription || "" }}
                systemFonts={["SharpGroteskMedium20", "SharpGroteskBook20"]}
                tagsStyles={tagStyles}
                baseStyle={styles.htmlTextStyle}
                contentWidth={width * 0.8}
              />
            </Block>

            <Block marginTop={2}>
              <Button label={t("common.continue")} onPress={handleContinue} type="primary" />
            </Block>
          </>
        )}
        {step === 2 && (
          <Animated.View entering={FadeIn}>
            <Typography variant="newH2">{t("dialogs.multiPersonServiceInfoTitle")}</Typography>
            <Typography type="secondary" variant="body" style={styles.infoDescription}>
              {t("dialogs.multiPersonServiceInfoMessage")}
            </Typography>
            <Block marginTop={2}>
              <Button label={t("common.understand")} onPress={handleClose} type="primary" />
            </Block>
          </Animated.View>
        )}
      </ScrollView>
    </BottomSheet>
  );
};

export default React.memo(CategoryQuestionAnswersSheet);

const styles = StyleSheet.create({
  bottomSheet: {
    paddingHorizontal: spacing.unit * 2.5,
    maxHeight: spacing.deviceHeight * 0.9,
    paddingVertical: spacing.unit * 2,
  },
  bottomSheetContainer: {
    justifyContent: "flex-end",
    padding: 0,
  },
  infoDescription: {
    marginTop: spacing.unit,
  },
  htmlTextStyle: {
    color: palette.font.secondary,
  },
  scrollStyles: {
    paddingVertical: spacing.unit * 2,
  },
});

const tagStyles = {
  a: typographyVariants.paragraph,
  b: typographyVariants.newBodyBold,
  li: typographyVariants.paragraph,
  p: typographyVariants.paragraph,
  span: typographyVariants.paragraph,
};
